import { apiConfig } from '@lib/api-config';
import { captureException } from '@src/utils/sm2/sentry';
import { convertStringToSnakeCase } from '@src/utils/snakeCase';
import Axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import { ZodError, ZodType } from 'zod';

const MAX_SENTRY_DATA_LENGTH = 500;

function _mapDataToSentry(e: unknown) {
  try {
    return JSON.stringify(e).slice(0, MAX_SENTRY_DATA_LENGTH);
    // eslint-disable-next-line unused-imports/no-unused-vars
  } catch (_) {
    return undefined;
  }
}

/**
 * Extracts unique field paths from a ZodError for Sentry tagging.
 *
 * Numeric segments (array indices) are stripped so that errors across multiple
 * array items deduplicate to a single path — e.g. `campaigns[0].name`,
 * `campaigns[1].name`, and `campaigns[2].name` all collapse to `campaigns.name`.
 *
 * Returns a JSON-stringified array of dot-separated path strings, or undefined
 * if serialization fails.
 */
function _getErrorPaths<T>(error: ZodError<T>) {
  try {
    return JSON.stringify(
      Array.from(
        new Set(
          error.issues.map((issue) =>
            issue.path.filter((segment) => typeof segment !== 'number').join('.'),
          ),
        ),
      ),
    );
    // eslint-disable-next-line unused-imports/no-unused-vars
  } catch (_) {
    return undefined;
  }
}

export function validateResponse<T>(
  schema: ZodType<T>,
  response: AxiosResponse<T>,
): AxiosResponse<T> {
  const { data, headers, status } = response;
  const parsedData = schema.safeParse(data);

  if (parsedData.error) {
    const request = response.request as undefined | XMLHttpRequest;

    captureException(parsedData.error, {
      tags: {
        url: request?.responseURL ?? response.config.url,
        paths: _getErrorPaths(parsedData.error),
      },
      extra: {
        status,
        // @Note axiosApiInstance runs camelcaseKeys on response.headers
        requestId: headers.xRequestId ?? headers['x-request-id'],
        smLogId: headers.smLogId ?? headers['sm-log-id'],
        data: _mapDataToSentry(data),
      },
    });

    if (process.env.NODE_ENV === 'development') {
      console.error(parsedData.error);
    }
  }

  return response;
}

export const axiosApiInstance = Axios.create({
  baseURL: apiConfig.axiosApiBaseUrl ?? '/pr-toolkit/api/v1',
});

// Modify response from snake_case to camelCase
axiosApiInstance.interceptors.response.use((response: AxiosResponse) => {
  return {
    ...response,
    headers: camelcaseKeys(response.headers),
    data: camelcaseKeys(response.data, { deep: true }),
  };
});

axiosApiInstance.interceptors.request.use((request: InternalAxiosRequestConfig) => {
  if (request.data instanceof FormData) {
    return request;
  }

  const { data, params } = request;

  if (data) {
    request.data = snakecaseKeys(data, { deep: true });
  }

  if (params) {
    const transformedParams = snakecaseKeys(params, { deep: true });

    // eslint-disable-next-line
    if (transformedParams.sort) {
      // eslint-disable-next-line
      transformedParams.sort = convertStringToSnakeCase(transformedParams.sort);
    }

    request.params = transformedParams;
  }

  return request;
});

// Discovery API

export const axiosDiscoveryApiInstance = Axios.create({
  baseURL: apiConfig.axiosDiscoveryBaseUrl ?? '/pr-toolkit/discovery/api/v1',
});

// Modify response from snake_case to camelCase
axiosDiscoveryApiInstance.interceptors.response.use((response: AxiosResponse) => {
  return { ...response, data: camelcaseKeys(response.data, { deep: true }) };
});

// Modify requests from camelCase to snake_case
axiosDiscoveryApiInstance.interceptors.request.use((request: InternalAxiosRequestConfig) => {
  if (request.data) {
    request = { ...request, data: snakecaseKeys(request.data, { deep: true }) };
  }

  if (request.params) {
    request = { ...request, params: snakecaseKeys(request.params, { deep: true }) };
  }

  return request;
});

// AI Assistant API

export const axiosAiAssistantApiInstance = Axios.create({
  baseURL: apiConfig.axiosAiAssistantBaseUrl ?? '/pr-toolkit/aiassistant/api/v1',
});

// Modify response from snake_case to camelCase
axiosAiAssistantApiInstance.interceptors.response.use((response: AxiosResponse) => {
  return { ...response, data: camelcaseKeys(response.data, { deep: true }) };
});

// Modify requests from camelCase to snake_case
axiosAiAssistantApiInstance.interceptors.request.use((request: InternalAxiosRequestConfig) => {
  if (request.data) {
    request = { ...request, data: snakecaseKeys(request.data, { deep: true }) };
  }

  if (request.params) {
    request = { ...request, params: snakecaseKeys(request.params, { deep: true }) };
  }

  return request;
});

// AI Visibility API

export const axiosAiVisibilityApiInstance = Axios.create({
  baseURL: apiConfig.axiosAiVisibilityBaseUrl ?? '/pr-toolkit/ai-visibility/api/v1',
});

// Modify response from snake_case to camelCase
axiosAiVisibilityApiInstance.interceptors.response.use((response: AxiosResponse) => {
  return { ...response, data: camelcaseKeys(response.data, { deep: true }) };
});

// Modify requests from camelCase to snake_case
axiosAiVisibilityApiInstance.interceptors.request.use((request: InternalAxiosRequestConfig) => {
  if (request.data) {
    request = { ...request, data: snakecaseKeys(request.data, { deep: true }) };
  }

  if (request.params) {
    request = { ...request, params: snakecaseKeys(request.params, { deep: true }) };
  }

  return request;
});
